<template>
  <CommonErrorBoundary>
    <template #default>
      <div class="w-full">
        <NuxtLayout>
          <div class="app-wrapper">
            <NuxtLoadingIndicator color="var(--color-primary)" />

            <NuxtPage />
          </div>
        </NuxtLayout>
      </div>

      <CommonDialogRequestError />
      <CommonStrapiPopup />
      <client-only>
        <CommonDialogNotSupportedBrowser />
      </client-only>
      <CommonDialogDisabledJavascript />
      <CommonMeilisearchPageBrowser />
      <CommonLoader />
      <SectionsSocketDebugger v-if="Number(INFO)" />

      <CommonNotivueInit />
    </template>

    <template #error="{ error }">
      <ErrorPage :error="error" />
      <CommonDialogDisabledJavascript />
    </template>
  </CommonErrorBoundary>
</template>

<script lang="ts" setup>
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { ref, provide, onMounted, watch } from 'vue'
import { SHOULD_USE_GLOBAL_BOUND_KEY } from '@autobid/ui/constants/SHOULD_USE_GLOBAL_BOUND_KEY'
import CommonErrorBoundary from '@autobid/ui/components/common/CustomErrorBoundary.vue'
import { useBody } from '@autobid/ui/composables/useBody'
import { changeLocale } from '@formkit/i18n'
import ErrorPage from './ErrorPage.vue'
import { randomDebounce } from '@autobid/ui/utils/randomDebounce'
import { useSetSentryUser } from '@autobid/sentry/src/composables/useSetSentryUser'

const setSentryUser = useSetSentryUser()
const { isAuthed, afterAuthCallback } = useAutobidAuth()
const { locale } = useI18n()
const { data: sessionData, getSession } = useAuth()
const runtimeConfig = useRuntimeConfig().public
const INFO = runtimeConfig.AUTOBID_WEBSOCKET?.INFO

changeLocale(locale.value)

if (isAuthed.value && process.client) {
  await getSession()
}

await afterAuthCallback(locale.value)

setSentryUser()

const shouldUseGlobalErrorBoundary = ref(true)
provide(SHOULD_USE_GLOBAL_BOUND_KEY, shouldUseGlobalErrorBoundary)

useBody()

const refreshToken = () => {
  if (!process.client || !isAuthed.value) return

  const diffInMs = new Date(sessionData.value?.tokenExpires).getTime() - new Date().getTime()
  // set random time of refreshing token in case of using multiple browser tabs (to avoid sending duplicated requests)
  randomDebounce(diffInMs - 1000 * 30, diffInMs - 1000 * 10, () => {
    console.log('refresh', new Date().toUTCString())
    getSession()
  })
}

watch([() => sessionData.value?.tokenExpires, isAuthed], () => {
  if (!sessionData.value?.tokenExpires) return

  refreshToken()
})

onMounted(refreshToken)

</script>
